import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader, StatCard } from '../../components/ui';
import { LuBriefcase, LuConciergeBell, LuCrown, LuEye, LuEyeOff, LuIdCard, LuKey, LuLifeBuoy, LuLock, LuLockOpen, LuPencil, LuShieldCheck, LuSmartphone, LuStore, LuUser, LuUsers, LuWallet } from 'react-icons/lu';

const ROLES = {
  admin: { label: 'Admin', icon: <LuShieldCheck aria-hidden />, couleur: '#B91C1C', bg: '#FEF2F2' },
  proprietaire: { label: 'Propriétaire', icon: <LuCrown aria-hidden />, couleur: '#6D28D9', bg: '#F5F3FF' },
  gerant: { label: 'Gérant', icon: <LuBriefcase aria-hidden />, couleur: '#1D4ED8', bg: '#EFF6FF' },
  serveur: { label: 'Serveur', icon: <LuConciergeBell aria-hidden />, couleur: '#C2410C', bg: '#FFF7ED' },
  caissier: { label: 'Caissier', icon: <LuWallet aria-hidden />, couleur: '#0E7490', bg: '#ECFEFF' },
};

export default function Utilisateurs() {
  const { utilisateur } = useAuth();
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [vue, setVue] = useState('liste');
  const [userSelectionne, setUserSelectionne] = useState(null);
  const [message, setMessage] = useState('');
  const [recherche, setRecherche] = useState('');
  const [filtreRole, setFiltreRole] = useState('tous');
  const [filtreEtab, setFiltreEtab] = useState('tous');
  const [page, setPage] = useState(1);
  const parPage = 10;

  const [form, setForm] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    mot_de_passe: '',
    role: 'serveur',
    etablissement_id: '',
    numero_piece_identite: '',
    type_piece_identite: 'CNI',
    contact_urgence_nom: '',
    contact_urgence_tel: ''
  });

  const [photoFichier, setPhotoFichier] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [afficherMotDePasse, setAfficherMotDePasse] = useState(false);

  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    try {
      const [etabRes, usersRes] = await Promise.all([
        API.get('/etablissements'),
        API.get('/utilisateurs')
        ]);
        setEtablissements(etabRes.data.etablissements || []);
        setUtilisateurs(usersRes.data.utilisateurs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  const handlePhoto = (e) => {
    const fichier = e.target.files[0];
    if (fichier) {
      setPhotoFichier(fichier);
      setPhotoPreview(URL.createObjectURL(fichier));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        if (form[key]) formData.append(key, form[key]);
      });
      if (photoFichier) formData.append('photo_profil', photoFichier);

      if (userSelectionne) {
        const data = Object.fromEntries(formData);
        if (!data.mot_de_passe) delete data.mot_de_passe;
        await API.put('/utilisateurs/' + userSelectionne.id, data);
        setMessage('✅ Utilisateur modifié avec succès !');
      } else {
        await API.post('/utilisateurs', formData);
        setMessage('✅ Utilisateur créé avec succès !');
      }

      chargerDonnees();
      setVue('liste');
      setUserSelectionne(null);
      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleModifier = (user) => {
    setUserSelectionne(user);
    setForm({
      nom: user.nom || '',
      prenom: user.prenom || '',
      telephone: user.telephone || '',
      mot_de_passe: '',
      role: user.role || 'serveur',
      etablissement_id: user.etablissement_id || '',
      numero_piece_identite: user.numero_piece_identite || '',
      type_piece_identite: user.type_piece_identite || 'CNI',
      contact_urgence_nom: user.contact_urgence_nom || '',
      contact_urgence_tel: user.contact_urgence_tel || ''
    });
    if (user.photo_profil) {
      setPhotoPreview(SERVER_URL + '/uploads/profils/' +
        user.photo_profil.replace(/\\/g, '/').split('/').pop());
    }
    setVue('formulaire');
  };

  const handleToggle = async (id, actif) => {
    if (!window.confirm(actif ? 'Désactiver cet utilisateur ?' : 'Réactiver cet utilisateur ?')) return;
    try {
      await API.patch('/utilisateurs/' + id + '/toggle');
      setMessage(actif ? '✅ Utilisateur désactivé !' : '✅ Utilisateur réactivé !');
      chargerDonnees();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const resetForm = () => {
    setForm({
      nom: '', prenom: '', telephone: '',
      mot_de_passe: '', role: 'serveur',
      etablissement_id: '', numero_piece_identite: '',
      type_piece_identite: 'CNI',
      contact_urgence_nom: '', contact_urgence_tel: ''
    });
    setPhotoFichier(null);
    setPhotoPreview(null);
    setAfficherMotDePasse(false);
  };

  const rolesAvecEtab = ['gerant', 'serveur', 'caissier'];

  const usersFiltres = utilisateurs.filter(u => {
    const matchRecherche = (u.nom + ' ' + u.prenom).toLowerCase().includes(recherche.toLowerCase()) ||
      u.telephone.includes(recherche);
    const matchRole = filtreRole === 'tous' || u.role === filtreRole;
    const matchEtab = filtreEtab === 'tous' || u.etablissement_id === parseInt(filtreEtab);
    return matchRecherche && matchRole && matchEtab;
  });

  const totalPages = Math.ceil(usersFiltres.length / parPage);
  const usersPage = usersFiltres.slice((page - 1) * parPage, page * parPage);
   
  const getEtabNom = (user) => {
    if (user.role === 'proprietaire') {
        const etabsDuProprietaire = etablissements.filter(
        e => e.proprietaire_id === user.id
        );
        if (etabsDuProprietaire.length === 0) return '—';
        return etabsDuProprietaire.map(e => e.nom).join(', ');
    }
    const etab = etablissements.find(e => e.id === user.etablissement_id);
    return etab ? etab.nom : '—';
    };

  if (chargement) return <Loader />;

  return (
    <AppLayout role={utilisateur?.role}>

      {/* CONTENU */}
      <div style={styles.contenu}>

        {message && (
          <div role="status" style={{
            ...styles.messageBox,
            background: message.startsWith('✅') ? '#ECFDF5' : '#FEF2F2',
            color: message.startsWith('✅') ? '#047857' : '#B91C1C'
          }}>{message.replace(/^(✅|❌|⚠️)\s*/, '')}</div>
        )}

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Utilisateurs</h1>
            <p style={styles.headerSousTitre}>
              {utilisateurs.length + ' utilisateur(s) • ' +
               utilisateurs.filter(u => u.actif).length + ' actifs'}
            </p>
          </div>
          {vue === 'liste' ? (
            <button onClick={() => { resetForm(); setUserSelectionne(null); setVue('formulaire'); }}
              style={styles.boutonAjouter}>
              + Nouvel Utilisateur
            </button>
          ) : (
            <button onClick={() => setVue('liste')} style={styles.boutonRetour}>
              ← Retour à la liste
            </button>
          )}
        </div>

        {/* STATS */}
        {vue === 'liste' && (
          <div style={styles.statsGrid}>
            {Object.entries(ROLES).map(([role, config]) => {
              const count = utilisateurs.filter(u => u.role === role).length;
              return (
                <StatCard key={role} icon={config.icon} label={config.label} value={count} color={config.couleur} />
              );
            })}
          </div>
        )}

        {/* ── LISTE ── */}
        {vue === 'liste' && (
          <>
            <div style={styles.filtres}>
              <input
                type="text"
                placeholder="Rechercher par nom ou téléphone..."
                value={recherche}
                onChange={(e) => { setRecherche(e.target.value); setPage(1); }}
                style={styles.rechercheInput}
              />
              <select
                value={filtreRole}
                onChange={(e) => { setFiltreRole(e.target.value); setPage(1); }}
                style={styles.filtreSelect}
              >
                <option value="tous">Tous les rôles</option>
                {Object.entries(ROLES).map(([role, config]) => (
                  <option key={role} value={role}>{config.label}</option>
                ))}
              </select>
              <select
                value={filtreEtab}
                onChange={(e) => { setFiltreEtab(e.target.value); setPage(1); }}
                style={styles.filtreSelect}
              >
                <option value="tous">Tous les établissements</option>
                {etablissements.map(e => (
                  <option key={e.id} value={e.id}>{e.nom}</option>
                ))}
              </select>
            </div>

            <div style={styles.section}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    {['Photo', 'Utilisateur', 'Rôle', 'Établissement',
                      'Téléphone', 'Pièce d\'identité', 'Statut', 'Actions'].map((h, i) => (
                      <th key={i} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {usersPage.map((user, i) => {
                    const role = ROLES[user.role] || ROLES.serveur;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                        <td style={styles.td}>
                          {user.photo_profil ? (
                            <img
                              src={SERVER_URL + '/uploads/profils/' +
                                user.photo_profil.replace(/\\/g, '/').split('/').pop()}
                              alt={user.nom}
                              style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={styles.avatarPlaceholder}>
                              {user.prenom?.[0]}{user.nom?.[0]}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700 }}>{user.prenom + ' ' + user.nom}</div>
                          {user.contact_urgence_nom && (
                            <div style={{ color: '#64748B', fontSize: 11 }}>
                              {' ' + user.contact_urgence_nom}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 700,
                            background: role.bg, color: role.couleur
                          }}>
                            {role.icon} {role.label}
                          </span>
                        </td>
                        <td style={styles.td}>
                        <div style={{ fontSize: 13 }}>
                            {getEtabNom(user)}
                        </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {user.telephone}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {user.numero_piece_identite ? (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748B' }}>{user.type_piece_identite}</div>
                              <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {user.numero_piece_identite}
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 700,
                            background: user.actif ? '#ECFDF5' : '#FEF2F2',
                            color: user.actif ? '#059669' : '#DC2626'
                          }}>
                            {user.actif ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => handleModifier(user)} style={styles.btnModifier} aria-label="Modifier" title="Modifier"><LuPencil aria-hidden style={{ verticalAlign: '-0.125em' }} /></button>
                            <button
                              onClick={() => handleToggle(user.id, user.actif)}
                              style={user.actif ? styles.btnDesactiver : styles.btnActiver}
                             aria-label={user.actif ? 'Désactiver' : 'Activer'} title={user.actif ? 'Désactiver' : 'Activer'}>
                              {user.actif ? <LuLock aria-hidden /> : <LuLockOpen aria-hidden />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {usersFiltres.length === 0 && (
                <div style={styles.vide}>
                  <div style={{ fontSize: 48 }}><LuUsers aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p>Aucun utilisateur trouvé</p>
                </div>
              )}

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ ...styles.pagBtn, opacity: page === 1 ? 0.4 : 1 }}>←</button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)} style={{
                      ...styles.pagNum,
                      background: page === i + 1 ? 'var(--accent)' : 'white',
                      color: page === i + 1 ? 'white' : '#475569'
                    }}>{i + 1}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{ ...styles.pagBtn, opacity: page === totalPages ? 0.4 : 1 }}>→</button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── FORMULAIRE ── */}
        {vue === 'formulaire' && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitre}>
              {userSelectionne ? "Modifier l'utilisateur" : 'Nouvel Utilisateur'}
            </h2>

            <form onSubmit={handleSubmit}>

              {/* SECTION 1 — PHOTO & IDENTITÉ */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuUser aria-hidden style={{ verticalAlign: '-0.125em' }} /> Identité</div>
                <div style={styles.photoSection}>

                  {/* PHOTO */}
                  <div style={styles.photoBox}>
                    {photoPreview ? (
                      <img src={photoPreview} alt="{user.prenom + ' ' + user.nom}"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <div style={styles.photoPlaceholderRond}>
                        <span style={{ fontSize: 36 }}><LuUser aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>
                        <span style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Photo</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={handlePhoto} style={styles.photoInput} />
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={styles.formGrid}>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}>Prénom *</label>
                        <input
                          type="text"
                          value={form.prenom}
                          onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                          style={styles.input}
                          placeholder="Ex: Moussa"
                          required
                        />
                      </div>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}>Nom *</label>
                        <input
                          type="text"
                          value={form.nom}
                          onChange={(e) => setForm({ ...form, nom: e.target.value })}
                          style={styles.input}
                          placeholder="Ex: Koné"
                          required
                        />
                      </div>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}><LuSmartphone aria-hidden style={{ verticalAlign: '-0.125em' }} /> Téléphone *</label>
                        <input
                          type="text"
                          value={form.telephone}
                          onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                          style={styles.input}
                          placeholder="Ex: +225 07 00 00 00"
                          required
                        />
                      </div>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}>
                          <LuKey aria-hidden style={{ verticalAlign: '-0.125em' }} /> {userSelectionne ? 'Nouveau mot de passe' : 'Mot de passe *'}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={afficherMotDePasse ? 'text' : 'password'}
                            value={form.mot_de_passe}
                            onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })}
                            style={{ ...styles.input, paddingRight: 44 }}
                            placeholder={userSelectionne ? 'Laisser vide pour ne pas changer' : 'Mot de passe...'}
                            required={!userSelectionne}
                          />
                          <button
                            type="button"
                            onClick={() => setAfficherMotDePasse(!afficherMotDePasse)}
                            aria-label={afficherMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            style={styles.btnOeil}
                          >
                            {afficherMotDePasse ? <LuEyeOff aria-hidden /> : <LuEye aria-hidden />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 2 — RÔLE & ÉTABLISSEMENT */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Rôle & Établissement</div>
                <div style={styles.formGrid}>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Rôle *</label>
                    <select
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value, etablissement_id: '' })}
                      style={styles.select}
                      required
                    >
                      {Object.entries(ROLES).map(([role, config]) => (
                        <option key={role} value={role}>
                          {config.label}
                        </option>
                      ))}
                    </select>
                    {form.role && (
                      <div style={{
                        marginTop: 6, padding: '6px 12px',
                        background: ROLES[form.role]?.bg,
                        color: ROLES[form.role]?.couleur,
                        borderRadius: 8, fontSize: 12, fontWeight: 700
                      }}>
                        {form.role === 'admin' && 'Accès total à tout'}
                        {form.role === 'proprietaire' && 'Gère ses établissements + employés + stocks + ventes'}
                        {form.role === 'gerant' && 'Gère produits + stocks + ventes de son établissement'}
                        {form.role === 'serveur' && 'Crée des ventes uniquement'}
                        {form.role === 'caissier' && 'Crée des ventes uniquement'}
                      </div>
                    )}
                  </div>

                  {rolesAvecEtab.includes(form.role) && (
                    <div style={styles.champGroupe}>
                      <label style={styles.label}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Établissement *</label>
                      <select
                        value={form.etablissement_id}
                        onChange={(e) => setForm({ ...form, etablissement_id: e.target.value })}
                        style={styles.select}
                        required
                      >
                        <option value="">Sélectionnez un établissement</option>
                        {etablissements.map(e => (
                          <option key={e.id} value={e.id}>{e.nom}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 3 — PIÈCE D'IDENTITÉ */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuIdCard aria-hidden style={{ verticalAlign: '-0.125em' }} /> Pièce d'identité</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Type de pièce</label>
                    <select
                      value={form.type_piece_identite}
                      onChange={(e) => setForm({ ...form, type_piece_identite: e.target.value })}
                      style={styles.select}
                    >
                      <option value="CNI">CNI — Carte Nationale d'Identité</option>
                      <option value="Passeport">Passeport</option>
                      <option value="Permis">Permis de conduire</option>
                      <option value="Carte_sejour">Carte de séjour</option>
                    </select>
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Numéro de pièce</label>
                    <input
                      type="text"
                      value={form.numero_piece_identite}
                      onChange={(e) => setForm({ ...form, numero_piece_identite: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: CI0123456789"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 4 — CONTACT D'URGENCE */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuLifeBuoy aria-hidden style={{ verticalAlign: '-0.125em' }} /> Contact d'urgence</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Nom du contact</label>
                    <input
                      type="text"
                      value={form.contact_urgence_nom}
                      onChange={(e) => setForm({ ...form, contact_urgence_nom: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: Marie Koné"
                    />
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Téléphone du contact</label>
                    <input
                      type="text"
                      value={form.contact_urgence_tel}
                      onChange={(e) => setForm({ ...form, contact_urgence_tel: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: +225 07 00 00 00"
                    />
                  </div>
                </div>
              </div>

              {/* ACTIONS */}
              <div style={styles.formActions}>
                <button type="button" onClick={() => setVue('liste')} style={styles.btnAnnuler}>
                  Annuler
                </button>
                <button type="submit" style={styles.btnSauvegarder}>
                  {userSelectionne ? 'Sauvegarder' : "Créer l'utilisateur"}
                </button>
              </div>

            </form>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', height: '100vh', fontSize: 24, color: '#475569'
  },
  contenu: { ...S.contenu, flex: 1 },
  messageBox: { ...S.messageBox, marginBottom: 16 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  boutonAjouter: S.boutonAjouter,
  boutonRetour: S.boutonRetour,
  statsGrid: { ...S.statsGrid, marginBottom: 20 },
  statCard: S.statCard,
  filtres: { ...S.filtres, marginBottom: 16 },
  rechercheInput: { ...S.rechercheInput },
  filtreSelect: { ...S.filtreSelect, minWidth: 160 },
  section: S.section,
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
  avatarPlaceholder: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'linear-gradient(135deg, #2563EB, #00B0FF)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 700, fontSize: 14
  },
  btnModifier: S.btnModifier,
  btnDesactiver: S.btnDesactiver,
  btnActiver: S.btnActiver,
  vide: S.vide,
  pagination: { ...S.pagination, marginTop: 16 },
  pagBtn: S.pagBtn,
  pagNum: { ...S.pagNum, width: 30 },
  formCard: S.formCard,
  formTitre: { ...S.formTitre, marginBottom: 24 },
  sectionForm: { ...S.sectionForm, marginBottom: 24 },
  sectionFormTitre: { ...S.sectionFormTitre, marginBottom: 16 },
  photoSection: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  photoBox: {
    width: 120, height: 120, borderRadius: '50%',
    border: '2px dashed #E2E8F0', overflow: 'hidden',
    cursor: 'pointer', position: 'relative', flexShrink: 0, background: '#F8FAFC'
  },
  photoPlaceholderRond: {
    width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center'
  },
  photoInput: {
    position: 'absolute', inset: 0, opacity: 0,
    cursor: 'pointer', width: '100%', height: '100%'
  },
  formGrid: S.formGrid,
  champGroupe: S.champGroupe,
  label: S.label,
  input: { ...S.input, width: '100%' },
  select: { ...S.select, width: '100%' },
  btnOeil: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 18
  },
  formActions: { ...S.formActions, marginTop: 16 },
  btnAnnuler: S.btnAnnuler,
  btnSauvegarder: S.btnSauvegarder
};